import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { nullAttrTypeOption } from '../GrowthAnalysis';
import ProfitGrowthDrivers from './ProfitGrowthDrivers';
import SubFilterDropdown from 'components/molecules/GrowthAnalysis/SubFilterDropdown';
import SubFilterCompareIcon from 'components/molecules/GrowthAnalysis/SubFilterCompareIcon';
import { FilterContextData } from 'contexts/FilterContext';
import { calculateContainerHeight, getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { cloneDeep, findLastIndex, uniqBy } from 'lodash';
import { useGrowthMarginDriversContext } from './context/GrowthMarginDriversContext';
import FundFlow from './FundFlow';
import FundFlowLegacy from './FundFlowLegacy';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import {
    createNodeFromSelection,
    createChildNodesFromApiData,
    addChildrenToNode,
    getNextHierarchyInfo,
    FundFlowTreeNode,
    truncateTreeAtAttributeType,
    HierarchyChangeEvent,
    getLeafNodesAtAttributeType,
    removeChildrenAtAttributeType,
    getLeafNodesAtAttributeType2,
    trimBelowDepth,
} from './data/fundFlowData';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import useDecompositionData from './hooks/useDecompositionData';
import useBatchDecomposition from './hooks/useBatchDecomposition';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import useProfitGrowthDriversData from './hooks/useProfitGrowthDriversData';
import { formatNumWithKMBAlt } from 'utils/helperMethods';
import { colorConstants } from 'config/colorConstants';
import RightArrowAlt from 'assets/svg/RightArrowAlt';
import useDecompositionGraphData from './hooks/useDecompositionGraphData';
import Loader from 'components/molecules/Loader';

const GrowthMarginDrivers = () => {
    const { appConfig } = useContext(UserContext);

    const profitPoolConfig = getConfig('profitPool', appConfig);
    const { defaultAttributeForData, excludedDimensions = [] } = profitPoolConfig;
    const {
        allDimAndAttributeTypeList,
        timePeriodLoading,
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
    } = useContext(FilterContextData);

    const [subFilterSelectedItems, setSubFilterSelectedItems] = useState([{ ...nullAttrTypeOption }]);
    const [subFilterDropdownOptions, setSubFilterDropdownOptions] = useState({ data: [], loading: false });
    const [activeCompareIcons, setActiveCompareIcons] = useState<any[]>([]);
    const [attributeTypeToShow, setAttributeTypesToShow] = useState<any[]>([]);

    const {
        selectedSubFilter,
        setSelectedSubFilter,
        selectedBarForViewBreakdown,
        setSelectedBarForViewBreakdown,
        selectedGrowthMarginDriver,
        fundFlowData,
        setFundFlowData,
        selectedFundFlowNode,
        setSelectedFundFlowNode,
        fundFlowLoading,
        setFundFlowLoading,
        decompositionFilterSelectedItems,
        setDecompositionFilterSelectedItems,
        memoisedDecompositionFilterDropdownOptions,
        selectedNodes,
        setSelectedNodes,
        setDecompositionGraphData,
    } = useGrowthMarginDriversContext();

    const { decompositionGraphData, isDecompositionGraphDataLoading } = useDecompositionGraphData();
    const chartHeight = calculateContainerHeight(2);
    const chartWidth = useContainerWidth({ containerId: 'decomposition-container' });

    const lastItemInHierarchy = useRef<any>({
        dim1: null,
        dim2: null,
        dim3: null,
    });

    // Track previous subFilterSelectedItems to detect changes
    const previousSubFilterSelectedItems = useRef<any[]>([]);
    // Track previous selectedGrowthMarginDriver to detect changes
    const previousSelectedGrowthMarginDriver = useRef<{ key: string; label: string } | null>(null);
    // Track previous idForRestOfComponent, timePeriodSelection, and singleSelectedTier to detect changes
    const previousIdForRestOfComponent = useRef<any>(null);
    const previousTimePeriodSelection = useRef<any>(null);
    const previousSingleSelectedTier = useRef<string[] | null>(null);

    // Add refs for Copy functionality
    const profitGrowthDriversRef = useRef<HTMLDivElement>(null);
    const decompositionRef = useRef<HTMLDivElement>(null);
    const decompositionRef2 = useRef<HTMLDivElement>(null);

    // State for decomposition API call parameters
    const [decompositionParams, setDecompositionParams] = useState<{
        parentAttributeType: string;
        parentDimension: string;
        nextAttributeType: string;
        nextDimension: string;
        nextIsAttribute?: boolean; // Add this
        enabled: boolean;
        nodeData?: FundFlowTreeNode | null;
    }>({
        parentAttributeType: '',
        parentDimension: '',
        nextAttributeType: '',
        nextDimension: '',
        nextIsAttribute: true, // Add this with default
        enabled: false,
        nodeData: null,
    });

    // Hook for fetching decomposition data (single node - used for node clicks and initial load)
    const {
        data: decompositionData,
        isPending: isDecompositionPending,
        isSuccess: isDecompositionSuccess,
    } = useDecompositionData(decompositionParams);

    // Hook for batch processing multiple nodes in parallel (used for subfilter add/change)
    const { processBatch, isProcessing: isBatchProcessing } = useBatchDecomposition();

    // Effect to create initial fund flow node when user selects a bar from waterfall chart
    useEffect(() => {
        if (selectedBarForViewBreakdown && selectedSubFilter) {
            const newNode = createNodeFromSelection(
                selectedBarForViewBreakdown,
                selectedGrowthMarginDriver?.key || 'profit_absolute',
                selectedSubFilter,
            );
            if (newNode) {
                setFundFlowData(newNode);
                // Automatically load the next hierarchy level for the initial node
                setSelectedFundFlowNode(newNode);
            }
        }
    }, [selectedBarForViewBreakdown, selectedSubFilter, selectedGrowthMarginDriver]);

    // Effect to automatically trigger decomposition for the initial node
    useEffect(() => {
        if (
            selectedFundFlowNode &&
            fundFlowData &&
            allDimAndAttributeTypeList?.dimentions &&
            !decompositionParams.enabled &&
            selectedFundFlowNode.id === fundFlowData.id // Only for root node
        ) {
            const currentAttributeType = selectedFundFlowNode.attributeType;
            const currentDimension = selectedFundFlowNode.dimension;

            if (currentAttributeType && currentDimension) {
                // Get next hierarchy info
                const nextHierarchy = getNextHierarchyInfo(
                    currentAttributeType,
                    currentDimension,
                    allDimAndAttributeTypeList.dimentions,
                );

                if (nextHierarchy) {
                    setDecompositionParams({
                        parentAttributeType: currentAttributeType,
                        parentDimension: currentDimension,
                        nextAttributeType: nextHierarchy.nextAttributeType,
                        nextDimension: nextHierarchy.nextDimension,
                        nextIsAttribute: nextHierarchy.nextIsAttribute, // Add this
                        enabled: true,
                        nodeData: selectedFundFlowNode,
                    });
                }
            }
        }
    }, [selectedFundFlowNode, fundFlowData, allDimAndAttributeTypeList?.dimentions, decompositionParams.enabled]);

    // Effect to update loading state based on API pending status and batch processing
    useEffect(() => {
        const isLoading = (isDecompositionPending && decompositionParams.enabled) || isBatchProcessing;
        setFundFlowLoading(isLoading);
    }, [isDecompositionPending, decompositionParams.enabled, isBatchProcessing]);

    // Effect to handle decomposition data response and add children to the fund flow tree
    // This is used for single node clicks and initial load
    useEffect(() => {
        if (isDecompositionSuccess && decompositionData?.data && selectedFundFlowNode && fundFlowData) {
            const childNodes = createChildNodesFromApiData(
                decompositionData.data,
                selectedGrowthMarginDriver?.key || 'profit_absolute',
                decompositionParams.nextAttributeType,
                decompositionParams.nextDimension,
                undefined, // parentTotal - will be calculated
                selectedFundFlowNode.id, // parentId
                selectedFundFlowNode.attributeExId, // parentAttributeExId
            );

            if (childNodes.length > 0) {
                // Use ID for precise matching (prevents matching wrong node with same name)
                const updatedTree = addChildrenToNode(
                    fundFlowData,
                    selectedFundFlowNode.id,
                    selectedFundFlowNode.name,
                    childNodes,
                );
                setFundFlowData(updatedTree);
            }

            // Reset decomposition params and selected node
            setDecompositionParams((prev) => ({ ...prev, enabled: false, nodeData: null }));
            setSelectedFundFlowNode(null);
        }
    }, [isDecompositionSuccess, decompositionData, selectedFundFlowNode, fundFlowData]);

    // Handler for fund flow node click
    const handleFundFlowNodeClick = useCallback(
        (nodeData: any) => {
            if (!nodeData || !allDimAndAttributeTypeList?.dimentions) return;

            const currentAttributeType = nodeData.attributeType;
            const currentDimension = nodeData.dimension;

            // Get next hierarchy info
            const nextHierarchy = getNextHierarchyInfo(
                currentAttributeType,
                currentDimension,
                allDimAndAttributeTypeList.dimentions,
            );

            if (nextHierarchy) {
                setSelectedFundFlowNode(nodeData as FundFlowTreeNode);
                setDecompositionParams({
                    parentAttributeType: currentAttributeType,
                    parentDimension: currentDimension,
                    nextAttributeType: nextHierarchy.nextAttributeType,
                    nextDimension: nextHierarchy.nextDimension,
                    nextIsAttribute: nextHierarchy.nextIsAttribute, // Add this
                    enabled: true,
                    nodeData: nodeData as FundFlowTreeNode,
                });
            }
        },
        [allDimAndAttributeTypeList?.dimentions],
    );

    // Handler for hierarchy change from FundFlow subfilter
    const handleFundFlowHierarchyChange = useCallback(
        async (event: HierarchyChangeEvent) => {
            if (!fundFlowData || !allDimAndAttributeTypeList?.dimentions) return;

            const { changedIndex, action, newItem, attributeTypesToTruncate, allSelectedItems, previousItem } = event;

            // Edge case: If user changes the first hierarchy (index 0), we need to rebuild the entire tree
            if (changedIndex === 0 && action === 'change') {
                // Clear the fund flow data - this will trigger a re-initialization
                // when user selects a new bar from the waterfall chart
                setFundFlowData(null);
                setSelectedFundFlowNode(null);
                setDecompositionParams((prev) => ({ ...prev, enabled: false, nodeData: null }));
                return;
            }

            // For remove action: Remove all nodes from the graph for the deselected hierarchy
            if (action === 'remove') {
                let updatedTree = cloneDeep(fundFlowData);

                // Remove children from all nodes at the deselected attribute type level
                // This removes the deselected hierarchy and all levels below it
                if (previousItem && previousItem.key) {
                    // First, find the parent attribute type (one level above the removed one)
                    const parentIndex = changedIndex - 1;
                    if (parentIndex >= 0 && allSelectedItems[parentIndex]) {
                        const parentAttrType = allSelectedItems[parentIndex].key;
                        // Remove children from nodes at the parent level (which are nodes of the deselected type)
                        updatedTree = removeChildrenAtAttributeType(updatedTree, parentAttrType);
                    } else {
                        // If no parent, truncate at the removed level
                        attributeTypesToTruncate.forEach((attrType) => {
                            updatedTree = truncateTreeAtAttributeType(updatedTree, attrType);
                        });
                    }
                } else {
                    // Fallback: truncate at each attribute type that should be removed
                    attributeTypesToTruncate.forEach((attrType) => {
                        updatedTree = truncateTreeAtAttributeType(updatedTree, attrType);
                    });
                }

                setFundFlowData(updatedTree);
                return;
            }

            // For change action at index > 0, we need to:
            // 1. Truncate the tree at the changed level (remove children from that level onwards)
            // 2. Call APIs for all leaf nodes at the parent level IN PARALLEL
            if (action === 'change' && changedIndex > 0) {
                let updatedTree = cloneDeep(fundFlowData);

                // Truncate at each attribute type that should be removed
                attributeTypesToTruncate.forEach((attrType) => {
                    updatedTree = truncateTreeAtAttributeType(updatedTree, attrType);
                });

                setFundFlowData(updatedTree);

                // Get the parent hierarchy info (the one before the changed index)
                if (allSelectedItems.length >= changedIndex && newItem) {
                    const parentItem = allSelectedItems[changedIndex - 1];
                    if (parentItem && parentItem.key && parentItem.dimension) {
                        // Find leaf nodes at the parent attribute type level
                        const leafNodes = getLeafNodesAtAttributeType(updatedTree, parentItem.key);

                        if (leafNodes.length > 0) {
                            // Process all nodes in parallel
                            const batchItems = leafNodes.map((node) => ({
                                node,
                                nextAttributeType: newItem.key,
                                nextDimension: newItem.dimension,
                                nextIsAttribute: newItem.isAttribute, // Add this
                            }));

                            const result = await processBatch(batchItems, updatedTree);
                            if (result) {
                                setFundFlowData(result);
                            }
                        }
                    }
                }
                return;
            }

            // For add action: Find all leaf nodes at parent level and call APIs for each IN PARALLEL
            // This is like user clicked on all graph nodes at the previous level
            if (action === 'add' && newItem) {
                // Get the parent item (the one before the new item)
                const parentIndex = changedIndex - 1;
                if (parentIndex >= 0 && allSelectedItems[parentIndex]) {
                    const parentItem = allSelectedItems[parentIndex];

                    if (parentItem.key && parentItem.dimension) {
                        // Find all leaf nodes at the parent attribute type level
                        const leafNodes = getLeafNodesAtAttributeType(fundFlowData, parentItem.key);

                        if (leafNodes.length > 0) {
                            // Process all nodes in parallel (apply filter + data for each)
                            const batchItems = leafNodes.map((node) => ({
                                node,
                                nextAttributeType: newItem.key,
                                nextDimension: newItem.dimension,
                                nextIsAttribute: newItem.isAttribute, // Add this
                            }));

                            const result = await processBatch(batchItems, fundFlowData);
                            if (result) {
                                setFundFlowData(result);
                            }
                        }
                    }
                }
            }
        },
        [fundFlowData, allDimAndAttributeTypeList?.dimentions, processBatch],
    );

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setSubFilterDropdownOptions({ data, loading: false });

                setSubFilterSelectedItems([
                    data.find((item: any) => item.label === defaultAttributeForData.attributeType) || data[0],
                    nullAttrTypeOption,
                ]);
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);

    // Get Previous year Data
    // Clear decomposition when waterfall chart's subfilter, Profit dropdown, or filter context values change
    useEffect(() => {
        const prevItems = previousSubFilterSelectedItems.current;
        const prevDriver = previousSelectedGrowthMarginDriver.current;
        const prevIdForRest = previousIdForRestOfComponent.current;
        const prevTimePeriod = previousTimePeriodSelection.current;
        const prevTier = previousSingleSelectedTier.current;

        const hasSubFilterChanged =
            prevItems.length > 0 && JSON.stringify(subFilterSelectedItems) !== JSON.stringify(prevItems);

        const hasDriverChanged =
            prevDriver !== null &&
            selectedGrowthMarginDriver !== null &&
            JSON.stringify(selectedGrowthMarginDriver) !== JSON.stringify(prevDriver);

        const hasIdForRestChanged =
            prevIdForRest !== null &&
            idForRestOfComponent !== null &&
            JSON.stringify(idForRestOfComponent) !== JSON.stringify(prevIdForRest);

        const hasTimePeriodChanged =
            prevTimePeriod !== null &&
            timePeriodSelection !== null &&
            JSON.stringify(timePeriodSelection) !== JSON.stringify(prevTimePeriod);

        const hasTierChanged =
            prevTier !== null &&
            singleSelectedTier !== null &&
            JSON.stringify(singleSelectedTier) !== JSON.stringify(prevTier);

        if (hasSubFilterChanged || hasDriverChanged || hasIdForRestChanged || hasTimePeriodChanged || hasTierChanged) {
            // Clear decomposition state - user must click a bar again
            setSelectedBarForViewBreakdown(null);
            setFundFlowData(null);
            setSelectedFundFlowNode(null);
            setDecompositionParams((prev) => ({ ...prev, enabled: false, nodeData: null }));
        }

        // Update refs to track current values
        previousSubFilterSelectedItems.current = subFilterSelectedItems;
        previousSelectedGrowthMarginDriver.current = selectedGrowthMarginDriver;
        previousIdForRestOfComponent.current = idForRestOfComponent;
        previousTimePeriodSelection.current = timePeriodSelection;
        previousSingleSelectedTier.current = singleSelectedTier;
    }, [
        subFilterSelectedItems,
        selectedGrowthMarginDriver,
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
    ]);

    const formatAttrTypeItems = (item: any) => {
        return {
            selectedAttributeType: item.label,
            selectedAttributeValue: item.value,
            isAttribute: item.isAttribute,
            dimension: item.dimension,
            dataset: [],
            disableHover:
                lastItemInHierarchy?.current[item.dimension] === null
                    ? true
                    : lastItemInHierarchy?.current?.[item.dimension]?.value <= item.value, //disable hover in bar chart for last item
            key: Math.random() + item.label, //adding a random key here so that the item gets re-rendered again
            dataKey: item?.key,
        };
    };
    const arrangeAttrType = (index: number) => {
        return [
            formatAttrTypeItems(subFilterSelectedItems[index]),
            formatAttrTypeItems(subFilterSelectedItems[index + 1]),
        ];
    };

    useEffect(() => {
        // resetInfiniteScroller();
        //if comparison available, compare scenarios and show all scenarios user has compared
        if (activeCompareIcons.length > 0) {
            const sortedActiveComparisons = [...activeCompareIcons];
            sortedActiveComparisons.sort((a, b) => a - b); //sort active comparison indices in ascending order
            let tempAttrTypeToShow: any[] = [];

            for (let i = 0; i < sortedActiveComparisons.length; i++) {
                tempAttrTypeToShow.push(...arrangeAttrType(sortedActiveComparisons[i]));
            }
            tempAttrTypeToShow = uniqBy(tempAttrTypeToShow, (e) => e.selectedAttributeValue); //remove duplicate sub filter items
            setAttributeTypesToShow(tempAttrTypeToShow);
            setSelectedSubFilter(tempAttrTypeToShow[0]);

            //else if atleast 1 subFilter selected, only show latest sub filter selection
        } else if (subFilterSelectedItems?.length > 1) {
            for (let i = subFilterSelectedItems.length - 1; i >= 0; i--) {
                //finding the latest sub filter selection
                if (subFilterSelectedItems[i]?.value !== null) {
                    const tempAttrTypeToShow: any[] = [formatAttrTypeItems(subFilterSelectedItems[i])];
                    setSelectedSubFilter(tempAttrTypeToShow[0]);
                    setAttributeTypesToShow(tempAttrTypeToShow);
                    return;
                }
            }
            //else dont show anything
        } else {
            setAttributeTypesToShow([]);
            setSelectedSubFilter(defaultAttributeForData);
        }
    }, [activeCompareIcons, subFilterSelectedItems]);

    const memoisedSubFilterDropdownOptions = useMemo(() => {
        const tempOptions: any[] = cloneDeep(subFilterDropdownOptions.data);
        subFilterSelectedItems.forEach((item) => {
            const attrTypeIndex = tempOptions.findIndex((elem: any) => elem.label === item?.label);
            if (attrTypeIndex > -1) tempOptions.splice(attrTypeIndex, 1);
        });
        if (tempOptions?.length > 0) {
            const dim3LastItemIndex = findLastIndex(tempOptions, (item) => item.dimension === 'dim3');
            lastItemInHierarchy.current.dim1 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim1')] || null;
            lastItemInHierarchy.current.dim2 =
                tempOptions[findLastIndex(tempOptions, (item) => item.dimension === 'dim2')] || null;
            lastItemInHierarchy.current.dim3 = dim3LastItemIndex === -1 ? null : tempOptions[dim3LastItemIndex];
        } //always store the last item in hierarchy for different dimensions
        return tempOptions;
    }, [subFilterSelectedItems, subFilterDropdownOptions]);

    const removeComparisonActiveItem = (index: number, arr: any[]) => {
        const idx = arr.indexOf(index);
        if (idx > -1) arr.splice(idx, 1);
    };

    const checkIfNullAttributePresent = (arr: any[]) => {
        let nullExists = false;
        for (let i = 0; i < arr.length; i++) {
            if (!arr[i].value) nullExists = true;
        }
        return nullExists;
    };
    const onSubFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const temp = [...subFilterSelectedItems];
        //if no item and val, it means event is triggered from clear icon
        if (!item && !val) {
            //if clearing last item and drilldown present

            temp.splice(index, 1); //remove item if cleared
            const tempActiveComparison = [...activeCompareIcons];
            removeComparisonActiveItem(index, tempActiveComparison); //remove comparison icon also on clearing sub filter
            if (index > 0) removeComparisonActiveItem(index - 1, tempActiveComparison); //remove both left and right side comparisons
            if (tempActiveComparison.includes(index + 1))
                tempActiveComparison.splice(tempActiveComparison.indexOf(index + 1), 1, index); //update the compare value
            setActiveCompareIcons(tempActiveComparison);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent && temp.length < subFilterDropdownOptions.data?.length) temp.push(nullAttrTypeOption); //add a new null sub filter if no null sub filter exists and its within the limit
        } else {
            //resetting apply filter object if there is no drilldown present or
            //if drilldown present and changing sub filters except the last subfilter dropdown, reset the apply filter object

            temp.splice(index, 1, item); //replace item if changed
            if (temp.length < subFilterDropdownOptions.data?.length && temp.length - 1 === index)
                temp.push(nullAttrTypeOption); //add a new null sub filter if its within the limit
        }

        setSubFilterSelectedItems(temp);
    };

    const compareSubFilterClickHandler = (index: number, active: boolean) => {
        localStorage.setItem('compare_clicked', 'true');
        const tempActiveComparison = [...activeCompareIcons];
        if (!active) {
            // tempActiveComparison.push(index); //keep multiple active comparisons at a time
            tempActiveComparison[0] = index; //only keep 1 active comparison icon at a time
        } else {
            removeComparisonActiveItem(index, tempActiveComparison);
        }
        setActiveCompareIcons(tempActiveComparison);
    };

    // Helper function to format attribute type words
    const formatWord = (word: string) => {
        return word.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Helper function to flatten FundFlow tree data for export
    const formatFundFlowData = useCallback((tree: FundFlowTreeNode | null): any[] => {
        if (!tree) return [];

        const rows: any[] = [];

        const traverse = (node: FundFlowTreeNode, level: number = 0) => {
            const indent = '  '.repeat(level);
            rows.push([
                `${indent}${node.name || ''}`,
                node.label || '',
                node.attributeType && typeof node.attributeType === 'string' ? formatWord(node.attributeType) : '',
                `${((node.variableValue || 0) * 100).toFixed(2)}%`,
                node.variableName || '',
            ]);

            if (node.children && node.children.length > 0) {
                node.children.forEach((child) => traverse(child, level + 1));
            }
        };

        traverse(tree);
        return rows;
    }, []);

    // Memoize formatted fund flow data - recalculate when fundFlowData changes
    const fundFlowExportData = useMemo(() => {
        return formatFundFlowData(decompositionGraphData);
    }, [decompositionGraphData, formatFundFlowData]);

    // Get profit growth drivers data for CSV export
    const {
        waterFallChartData,
        currentYearData,
        previousYearData,
        isSuccess: isProfitGrowthDriversSuccess,
    } = useProfitGrowthDriversData();

    // Format profit growth drivers data for CSV/XLSX export
    const profitGrowthDriversExportData = useMemo(() => {
        if (!isProfitGrowthDriversSuccess || !waterFallChartData || waterFallChartData.length === 0) {
            return [];
        }

        const rows: any[] = [];

        // Add Year Ago row (as array for XLSX compatibility)
        rows.push(['Year Ago', formatNumWithKMBAlt(previousYearData || 0), '']);

        // Add waterfall chart data rows
        waterFallChartData.forEach((item: any) => {
            const value = Array.isArray(item[selectedGrowthMarginDriver?.key || 'profit_absolute'])
                ? item[selectedGrowthMarginDriver?.key || 'profit_absolute'][1] -
                  item[selectedGrowthMarginDriver?.key || 'profit_absolute'][0]
                : item[selectedGrowthMarginDriver?.key || 'profit_absolute'] || 0;

            const growthLabel =
                item.trend === 'positive' ? 'Positive Growth' : item.trend === 'negative' ? 'Negative Growth' : '';

            rows.push([item.attributeDesc || '', formatNumWithKMBAlt(value), growthLabel]);
        });

        // Add Current Year row
        rows.push(['Current Year', formatNumWithKMBAlt(currentYearData || 0), '']);

        return rows;
    }, [
        waterFallChartData,
        currentYearData,
        previousYearData,
        isProfitGrowthDriversSuccess,
        selectedGrowthMarginDriver,
    ]);

    const DecompositionFilterIcon = () => {
        return (
            <div>
                <RightArrowAlt stroke={colorConstants.violet} />
            </div>
        );
    };

    const onDecompositionFilterOptionsChange = (val: any, item: any, index: number, filter?: any) => {
        const DECOMPOSITION_LIMIT = 5;
        let temp = [...decompositionFilterSelectedItems];
        if (!item && !val) {
            // if (decompositionFilterSelectedItems.length >= DECOMPOSITION_LIMIT) {
            //     temp.splice(index, 1, nullAttrTypeOption);
            // } else {
            //     temp.splice(index, 1);
            // }
            if (index < temp.length - 1) {
                temp = temp.slice(0, index);
            } else {
                temp.splice(index, 1, nullAttrTypeOption);
            }
            temp.splice(index, 1);
            const isNullPresent = checkIfNullAttributePresent(temp);
            if (!isNullPresent) temp.push(nullAttrTypeOption);
            setDecompositionGraphData(null);
            const trimmedGraphData = trimBelowDepth(decompositionGraphData, index - 1);
            setDecompositionGraphData(trimmedGraphData);
        } else {
            if (decompositionFilterSelectedItems.length >= DECOMPOSITION_LIMIT) {
                if (index < temp.length - 1) {
                    temp.splice(index, 0, item);
                    temp.splice(temp.length - 1, 1);
                    temp = temp.slice(0, index + 1);
                    temp.push(nullAttrTypeOption);
                } else {
                    temp.splice(index, 0, item);
                    temp.splice(temp.length - 1, 1);
                }
            } else {
                if (index < temp.length - 1) {
                    temp.splice(index, 0, item);
                    temp = temp.slice(0, index + 1);
                    const isNullPresent = checkIfNullAttributePresent(temp);
                    if (!isNullPresent) temp.push(nullAttrTypeOption);
                } else {
                    temp.splice(index, 0, item);
                }
            }
            const parentAttributeLevel = temp[index - 1];
            const nextAttributeLevel = temp[index];
            const leafNodesAtParentLevel = getLeafNodesAtAttributeType2(
                decompositionGraphData,
                parentAttributeLevel.key,
            );
            const updatedLeafNodesAtParentLevel = leafNodesAtParentLevel.map((_leafNode) => {
                return {
                    ..._leafNode,
                    nextAttributeType: nextAttributeLevel?.key,
                    nextDimension: nextAttributeLevel?.dimension,
                    nextIsAttribute: nextAttributeLevel?.isAttribute,
                };
            });
            setSelectedNodes(uniqBy(updatedLeafNodesAtParentLevel, 'id'));
        }
        setDecompositionFilterSelectedItems(temp);
    };

    return (
        <>
            <div className="flex gap-2 mb-4">
                {subFilterSelectedItems.map((item, index) => {
                    return (
                        <div key={index} className="flex gap-2 items-center">
                            <SubFilterDropdown
                                loading={allDimAndAttributeTypeList?.loading || timePeriodLoading || false}
                                onChange={onSubFilterOptionsChange}
                                key={index}
                                index={index}
                                options={memoisedSubFilterDropdownOptions}
                                value={item.label}
                                from={'growth-analysis'}
                            />
                            {subFilterSelectedItems?.length - 1 !== index && (
                                <SubFilterCompareIcon
                                    disabled={subFilterSelectedItems[index + 1]?.label === null}
                                    onClick={compareSubFilterClickHandler}
                                    index={index}
                                    active={activeCompareIcons.includes(index)}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
            <div
                className={`border border-borderLinePurpule bg-white p-4  rounded-lg            
            `}
            >
                <p className="font-semibold text-2xl">{'Growth Margin Drivers'}</p>
                <div
                    className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-4 w-full"
                    ref={profitGrowthDriversRef}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex-1" />
                        <div className="flex justify-end gap-2">
                            <CopyToClipBoard refOfComponent={profitGrowthDriversRef} removeScrollbar />
                            {isProfitGrowthDriversSuccess && profitGrowthDriversExportData.length > 0 && (
                                <DownloadExcel
                                    title="Profit Growth Drivers"
                                    excelData={profitGrowthDriversExportData}
                                    headers={['Attribute', 'Value', 'Growth']}
                                    excludeFilters={true}
                                />
                            )}
                        </div>
                    </div>
                    <ProfitGrowthDrivers />
                </div>
                <div
                    className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-4 w-full"
                    id="decomposition-container-legacy"
                    ref={decompositionRef2}
                >
                    {/* Header Section */}
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-lg font-semibold">{`Decomposition`}</p>
                        {decompositionGraphData && (
                            <div className="flex justify-end gap-2">
                                <CopyToClipBoard refOfComponent={decompositionRef2} removeScrollbar />
                                <DownloadExcel
                                    title="Decomposition"
                                    excelData={fundFlowExportData}
                                    headers={[
                                        'Name',
                                        'Value',
                                        'Currency',
                                        'Attribute Type',
                                        'Percentage',
                                        'Variable',
                                        'Status',
                                    ]}
                                    excludeFilters={true}
                                />
                            </div>
                        )}
                    </div>
                    {isDecompositionGraphDataLoading ? (
                        <div className="flex items-center justify-center text-gray-500" style={{ height: chartHeight }}>
                            <Loader />
                        </div>
                    ) : (
                        <>
                            {/* Decomposition Filter Section */}
                            {decompositionGraphData ? (
                                <>
                                    <div className="flex gap-2 mb-4">
                                        {decompositionFilterSelectedItems.map((item, index) => {
                                            return (
                                                <div
                                                    key={index}
                                                    className={`flex gap-2 items-center ${
                                                        index === 0 && item?.value !== null ? 'pointer-events-none' : ''
                                                    }`}
                                                >
                                                    <SubFilterDropdown
                                                        loading={
                                                            allDimAndAttributeTypeList?.loading ||
                                                            timePeriodLoading ||
                                                            false
                                                        }
                                                        onChange={onDecompositionFilterOptionsChange}
                                                        key={index}
                                                        index={index}
                                                        options={memoisedDecompositionFilterDropdownOptions}
                                                        value={item.label}
                                                        from={'growth-analysis'}
                                                    />
                                                    {decompositionFilterSelectedItems?.length - 1 !== index && (
                                                        <DecompositionFilterIcon />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {/* Graph Section */}
                                    <FundFlowLegacy
                                        data={decompositionGraphData}
                                        height={chartHeight}
                                        width={chartWidth}
                                        containerId="decomposition-container-legacy"
                                        onNodeClick={handleFundFlowNodeClick}
                                        loading={fundFlowLoading}
                                        decompositionFilterSelectedItems={decompositionFilterSelectedItems}
                                        decompositionFilterDropdownOptions={memoisedDecompositionFilterDropdownOptions}
                                    />
                                </>
                            ) : (
                                <div
                                    className="flex items-center justify-center text-gray-500"
                                    style={{ height: chartHeight }}
                                >
                                    <p>Select a bar from the chart above to view decomposition</p>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* <div
                    className="mt-4 flex flex-col border border-[#0000000F] rounded-lg px-4 py-4 w-full"
                    id="decomposition-container"
                    ref={decompositionRef}
                >
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-lg font-semibold">{`Decomposition`}</p>
                        {fundFlowData && (
                            <div className="flex justify-end gap-2">
                                <CopyToClipBoard refOfComponent={decompositionRef} removeScrollbar />
                                <DownloadExcel
                                    title="Decomposition"
                                    excelData={fundFlowExportData}
                                    headers={[
                                        'Name',
                                        'Value',
                                        'Currency',
                                        'Attribute Type',
                                        'Percentage',
                                        'Variable',
                                        'Status',
                                    ]}
                                    excludeFilters={true}
                                />
                            </div>
                        )}
                    </div>
                    <FundFlow
                        data={fundFlowData}
                        height={chartHeight}
                        width={chartWidth}
                        // Must be unique (the outer wrapper also uses `decomposition-container`)
                        containerId="decomposition-graph-container"
                        onNodeClick={handleFundFlowNodeClick}
                        loading={fundFlowLoading}
                        // Pass subfilter props to FundFlow for its own subfilter state
                        initialSubFilterItems={subFilterSelectedItems}
                        subFilterDropdownOptions={subFilterDropdownOptions}
                        allDimAndAttributeTypeLoading={allDimAndAttributeTypeList?.loading}
                        timePeriodLoading={timePeriodLoading}
                        // Handler for hierarchy changes from subfilter
                        onHierarchyChange={handleFundFlowHierarchyChange}
                    />
                </div> */}
            </div>
        </>
    );
};

export default GrowthMarginDrivers;
